import React, { useEffect, useState, useRef, useCallback } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getCountyDisplayName } from '../../util/countyHelpers';

import {
  Page,
  LayoutSingleColumn,
  IconSpinner,
  NamedLink,
  VendorCard,
  VendorMap,
} from '../../components';
import LocationAutocompleteInputImpl from '../../components/LocationAutocompleteInput/LocationAutocompleteInputImpl';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  fetchVendorsThunk,
  selectVendorThunk,
  setVendorSelections,
  selectVendors,
  selectFetchInProgress,
  selectFetchError,
  selectCategory,
  selectCounty,
  selectServices,
  selectAddress,
  selectLastFetchKey,
  selectVendorSelections,
  selectSelectInProgress,
  selectSelectError,
  releaseVendorThunk,
  selectReleaseInProgress,
  selectReleaseError,
} from './MatchedVendorsPage.duck';
import YourPicks from './YourPicks';

import { apiBaseUrl } from '../../util/api';
import css from './MatchedVendorsPage.module.css';

// Category labels for display
const CATEGORY_LABELS = {
  tree_service: 'Tree Service & Removal',
  brush_clearing: 'Brush Clearing',
  chipping_debris: 'Chipping & Debris Removal',
  dsc: 'Defensible Space Clearing',
  frl: 'Fire-Resistant Landscaping',
  grazing: 'Grazing',
  hh: 'Home Hardening',
  heavy_machinery: 'Heavy Machinery',
  consultation: 'Consultation',
};

const CATEGORY_KEYS = Object.keys(CATEGORY_LABELS);
const MAX_ACTIVE_VENDORS = 3;

const MatchedVendorsPageComponent = (props) => {
  const {
    location,
    history,
    scrollingDisabled,
    vendors,
    fetchInProgress,
    fetchError,
    category,
    county,
    services,
    address,
    lastFetchKey,
    onFetchVendors,
    // Vendor selection props
    vendorSelections,
    selectInProgress,
    selectError,
    onSelectVendor,
    onSetVendorSelections,
    releaseInProgress,
    releaseError,
    onReleaseVendor,
  } = props;

  const intl = useIntl();
  const searchParams = new URLSearchParams(location.search);
  const urlCategory = searchParams.get('category');
  const urlAddress = searchParams.get('address') || '';
  const urlRequestId = searchParams.get('requestId');
  const isSelectionMode = searchParams.get('from') === 'request' && !!urlRequestId;

  const [locationValue, setLocationValue] = useState({
    search: urlAddress,
    predictions: [],
    selectedPlace: null,
  });
  const [activeVendorId, setActiveVendorId] = useState(null);
  const [showMobileMap, setShowMobileMap] = useState(false);
  const [nameQuery, setNameQuery] = useState('');
  const vendorCardRefs = useRef({});

  // Sync location input when URL changes
  useEffect(() => {
    setLocationValue({ search: urlAddress, predictions: [], selectedPlace: null });
  }, [urlAddress]);

  // Fetch vendors when URL params change. Skipped when SSR loadData already
  // fetched this exact param set, so hydration doesn't refetch and flicker.
  useEffect(() => {
    const cat = searchParams.get('category');
    const cty = searchParams.get('county');
    const svc = searchParams.get('services');
    const addr = searchParams.get('address');
    const paramsKey = [cat, cty, svc, addr].map(v => v || '').join('|');
    if (paramsKey === lastFetchKey) {
      return;
    }
    onFetchVendors({ category: cat, county: cty, services: svc, address: addr });
  }, [location.search]);

  // Load the request's picks (with names and reply status) in selection mode. Also
  // re-run after each pick, so a new pick gets its name, status and reply deadline.
  const fetchSelections = useCallback(async () => {
    if (!isSelectionMode || !urlRequestId) return;
    try {
      const resp = await window.fetch(`${apiBaseUrl()}/api/request-selections?requestId=${urlRequestId}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (resp.ok) {
        const data = await resp.json();
        onSetVendorSelections(data.vendorSelections || []);
      }
    } catch (e) {
      // Selections will start empty — not critical
    }
  }, [isSelectionMode, urlRequestId, onSetVendorSelections]);

  useEffect(() => {
    fetchSelections();
  }, [fetchSelections]);

  // Navigate with updated params
  const navigateWithParams = (newCategory, newAddress) => {
    const params = new URLSearchParams();
    if (newCategory) params.set('category', newCategory);
    if (newAddress) params.set('address', newAddress);
    if (urlRequestId) params.set('requestId', urlRequestId);
    if (isSelectionMode) params.set('from', 'request');
    history.push({ pathname: '/vendors', search: params.toString() });
  };

  const locationSearchText = locationValue?.selectedPlace?.address || locationValue?.search || '';

  const handleCategoryClick = (cat) => {
    navigateWithParams(cat, locationSearchText);
  };

  const handleLocationUpdate = () => {
    navigateWithParams(urlCategory, locationSearchText.trim());
  };

  const handleLocationChange = (newValue) => {
    setLocationValue(newValue);
    if (newValue.selectedPlace?.address) {
      navigateWithParams(urlCategory, newValue.selectedPlace.address);
    }
  };

  // Vendor selection handler
  const handleSelectVendor = useCallback((vendorId) => {
    if (!urlRequestId) return;
    Promise.resolve(onSelectVendor({ requestId: urlRequestId, vendorId })).then(fetchSelections);
  }, [urlRequestId, onSelectVendor, fetchSelections]);

  // Release a pick with no reply after 24 hours so the homeowner can choose someone
  // else (the server enforces the same rule; see server/api-util/pickStatus.js).
  const handleReplaceVendor = useCallback((vendorId) => {
    if (!urlRequestId) return;
    onReleaseVendor({ requestId: urlRequestId, vendorId });
  }, [urlRequestId, onReleaseVendor]);

  // Navigate to the vendor profile, preserving requestId when present
  const goToVendorProfile = useCallback((vendorId) => {
    const search = urlRequestId ? `?requestId=${urlRequestId}` : '';
    history.push(`/u/${vendorId}${search}`);
  }, [history, urlRequestId]);

  // Clicking a card or map flag navigates to the vendor profile
  const handleCardClick = useCallback((vendorId) => {
    goToVendorProfile(vendorId);
  }, [goToVendorProfile]);

  const handleMapVendorClick = useCallback((vendorId) => {
    goToVendorProfile(vendorId);
  }, [goToVendorProfile]);

  const handleCardHover = useCallback((vendorId) => {
    setActiveVendorId(vendorId);
  }, []);

  const handleCardLeave = useCallback(() => {
    setActiveVendorId(null);
  }, []);

  // Selection state calculations
  const pendingSelections = vendorSelections.filter(s => s.status === 'pending');
  const activeCount = pendingSelections.length;
  const maxReached = activeCount >= MAX_ACTIVE_VENDORS;
  const hasAnySelection = vendorSelections.length > 0;

  // Helper: check if vendor is already selected (any status)
  const getVendorSelectionStatus = (vendorId) => {
    const sel = vendorSelections.find(s => s.vendorId === vendorId);
    return sel ? sel.status : null;
  };

  const categoryLabel = category ? (CATEGORY_LABELS[category] || category) : null;
  const countyLabel = county ? getCountyDisplayName(county) : null;
  const addressLabel = address || null;
  const hasFilters = categoryLabel || countyLabel || addressLabel || services;

  const title = hasFilters
    ? intl.formatMessage(
        { id: 'MatchedVendorsPage.title' },
        { category: categoryLabel || 'Local Services' }
      )
    : intl.formatMessage({ id: 'MatchedVendorsPage.directoryTitle' });

  // Client-side name filter over the fetched vendors
  const normalizedNameQuery = nameQuery.trim().toLowerCase();
  const visibleVendors = normalizedNameQuery
    ? vendors.filter(v =>
        `${v.businessName || ''} ${v.displayName || ''}`.toLowerCase().includes(normalizedNameQuery)
      )
    : vendors;
  const noNameMatches = vendors.length > 0 && visibleVendors.length === 0;

  const vendorsWithLocation = visibleVendors.filter(v => v.geolocation?.lat && v.geolocation?.lng);
  const hasMapVendors = vendorsWithLocation.length > 0;

  return (
    <Page
      title={title}
      description="Find local wildfire mitigation professionals in Northern California. Browse by service and location — defensible space, tree work, chipping, home hardening, and more."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          {/* Selection mode banner */}
          {isSelectionMode && (
            <div className={css.selectionBanner}>
              <div className={css.selectionBannerContent}>
                <div className={css.selectionBannerIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className={css.selectionBannerText}>
                  <h2 className={css.selectionBannerTitle}>Your request has been submitted!</h2>
                  <p className={css.selectionBannerSubtitle}>
                    Now choose up to 3 contractors to send it to.
                  </p>
                </div>
              </div>
              <div className={css.selectionCounter}>
                <span className={css.selectionCountNumber}>{activeCount}</span>
                <span className={css.selectionCountLabel}> of {MAX_ACTIVE_VENDORS} contractors selected</span>
              </div>
            </div>
          )}

          {isSelectionMode && (
            <YourPicks
              picks={vendorSelections}
              vendors={vendors}
              releaseInProgress={releaseInProgress}
              releaseError={releaseError}
              onReplace={handleReplaceVendor}
            />
          )}

          {/* Selection errors — 401 means the session expired (e.g. a stale
              bookmark); the endpoints require the homeowner's session. */}
          {isSelectionMode && selectError && (
            <div className={css.selectionErrorBanner}>
              {selectError.status === 401 ? (
                <>
                  Your session has expired.{' '}
                  <NamedLink
                    name="LoginPage"
                    to={{ state: { from: `${location.pathname}${location.search}` } }}
                    className={css.selectionErrorLink}
                  >
                    Log in
                  </NamedLink>{' '}
                  to send your request to contractors.
                </>
              ) : (
                selectError.message || 'Something went wrong sending your request. Please try again.'
              )}
            </div>
          )}

          {/* Page Header */}
          <div className={css.pageHeader}>
            <h1 className={css.pageTitle}>
              {visibleVendors.length > 0 && !fetchInProgress
                ? `${visibleVendors.length} Contractor${visibleVendors.length !== 1 ? 's' : ''} Found`
                : fetchInProgress
                  ? 'Searching...'
                  : noNameMatches
                    ? 'No Name Matches'
                    : hasFilters ? 'Contractors Coming Soon' : 'Contractor Directory'
              }
            </h1>
            <p className={css.pageSubtext}>
              Local professionals for defensible space, tree work, home hardening, and more.
            </p>
            <p className={css.verifyNotice}>
              CA Fire Defense does not vet, verify, or endorse contractors. Before hiring, verify any
              contractor’s license, bonding, and workers’ compensation for free at{' '}
              <a
                className={css.verifyNoticeLink}
                href="https://www.cslb.ca.gov/onlineservices/checklicenseII/checklicense.aspx"
                target="_blank"
                rel="noopener noreferrer"
              >
                cslb.ca.gov ↗
              </a>
            </p>
            {(categoryLabel || addressLabel) && !fetchInProgress && (
              <p className={css.pageSubtitle}>
                {categoryLabel && <span className={css.filterTag}>{categoryLabel}</span>}
                {addressLabel && <span className={css.filterTag}>{addressLabel}</span>}
              </p>
            )}

            {/* Mobile map toggle */}
            {vendors.length > 0 && !fetchInProgress && (
              <button
                className={css.mobileMapToggle}
                onClick={() => setShowMobileMap(!showMobileMap)}
                type="button"
              >
                {showMobileMap ? 'Show List' : 'Show Map'}
              </button>
            )}
          </div>

          {/* Mobile map view */}
          {showMobileMap && (
            <div className={css.mobileMapContainer}>
              <VendorMap
                vendors={visibleVendors}
                activeVendorId={activeVendorId}
                onVendorClick={handleMapVendorClick}
              />
            </div>
          )}

          {/* Selection mode: done bar (sticky) */}
          {isSelectionMode && hasAnySelection && (
            <div className={css.selectionDoneBar}>
              <NamedLink name="DashboardPage" className={css.selectionDoneButton}>
                Done — View My Requests
              </NamedLink>
              <button
                className={css.selectionSkipLink}
                onClick={() => history.push('/dashboard')}
                type="button"
              >
                Skip for now — I'll choose later
              </button>
            </div>
          )}

          {/* Split-view layout */}
          <div className={classNames(css.splitLayout, { [css.mobileMapVisible]: showMobileMap })}>
            {/* Left Panel: Filters + Vendor List */}
            <div className={css.listPanel}>
              {/* Intro text */}
              {!isSelectionMode && (
                <div className={css.searchIntro}>
                  <p className={css.searchIntroText}>
                    Enter your location below to find wildfire mitigation professionals near you.
                    Filter by category to match your specific needs.
                  </p>
                </div>
              )}

              {/* Filters bar */}
              <div className={css.filtersBar}>
                {/* Location Input */}
                <div className={css.locationSection}>
                  <label className={css.filterLabel}>Location</label>
                  <div className={css.locationInputGroup}>
                    <LocationAutocompleteInputImpl
                      className={css.locationAutocomplete}
                      inputClassName={css.locationInput}
                      predictionsClassName={css.locationPredictions}
                      iconClassName={css.locationIconHidden}
                      placeholder="City, ZIP, or address"
                      useDefaultPredictions={false}
                      useDarkText
                      input={{
                        name: 'vendorLocation',
                        value: locationValue,
                        onChange: handleLocationChange,
                        onFocus: () => {},
                        onBlur: () => {},
                      }}
                      meta={{ touched: false, error: null }}
                    />
                    <button
                      className={css.locationButton}
                      onClick={handleLocationUpdate}
                      type="button"
                    >
                      Update
                    </button>
                  </div>
                </div>

                {/* Category filter */}
                <div className={css.categorySection}>
                  <label className={css.filterLabel}>Category</label>
                  <select
                    className={css.categorySelect}
                    value={urlCategory || ''}
                    onChange={(e) => handleCategoryClick(e.target.value || null)}
                  >
                    <option value="">All Categories</option>
                    {CATEGORY_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {CATEGORY_LABELS[key]}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name search */}
                <div className={css.nameSection}>
                  <label className={css.filterLabel} htmlFor="vendorNameSearch">Search by Name</label>
                  <div className={css.nameInputGroup}>
                    <input
                      id="vendorNameSearch"
                      className={css.nameInput}
                      type="text"
                      placeholder="Business name"
                      value={nameQuery}
                      onChange={(e) => setNameQuery(e.target.value)}
                    />
                    {nameQuery && (
                      <button
                        className={css.nameClearButton}
                        onClick={() => setNameQuery('')}
                        type="button"
                        aria-label="Clear name search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Vendor list */}
              <div className={css.vendorList}>
                {fetchInProgress ? (
                  <div className={css.loadingContainer}>
                    <IconSpinner className={css.spinner} />
                    <p className={css.loadingText}>Finding contractors in your area...</p>
                  </div>
                ) : fetchError ? (
                  <div className={css.errorContainer}>
                    <div className={css.errorIcon}>!</div>
                    <h2 className={css.errorTitle}>Unable to Load Contractors</h2>
                    <p className={css.errorText}>
                      We couldn't load matching contractors at this time. Please try again later.
                    </p>
                    <NamedLink name="LandingPage" className={css.errorLink}>
                      Return to Home
                    </NamedLink>
                  </div>
                ) : noNameMatches ? (
                  <div className={css.emptyState}>
                    <h2 className={css.emptyTitle}>No contractors match "{nameQuery.trim()}"</h2>
                    <p className={css.emptyText}>
                      Try a different spelling, or clear the search to see all {vendors.length} contractor{vendors.length !== 1 ? 's' : ''}.
                    </p>
                    <div className={css.emptyActions}>
                      <button
                        className={css.secondaryButton}
                        onClick={() => setNameQuery('')}
                        type="button"
                      >
                        Clear Search
                      </button>
                    </div>
                  </div>
                ) : vendors.length > 0 ? (
                  <>
                    <div className={css.vendorGrid}>
                      {visibleVendors.map((vendor, index) => {
                        const selectionStatus = getVendorSelectionStatus(vendor.id);
                        const isSelected = !!selectionStatus;
                        const isPending = selectionStatus === 'pending';
                        const isSelecting = selectInProgress[vendor.id];
                        const isDisabled = isSelectionMode && maxReached && !isSelected;

                        return (
                          <div
                            key={vendor.id}
                            ref={(el) => { vendorCardRefs.current[vendor.id] = el; }}
                            className={classNames(css.vendorCardWrapper, {
                              [css.vendorCardActive]: vendor.id === activeVendorId,
                              [css.vendorCardSelected]: isSelectionMode && isPending,
                              [css.vendorCardDisabled]: isDisabled,
                            })}
                            style={{ animationDelay: `${index * 0.08}s` }}
                            onMouseEnter={() => handleCardHover(vendor.id)}
                            onMouseLeave={handleCardLeave}
                            onClick={() => handleCardClick(vendor.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleCardClick(vendor.id);
                              }
                            }}
                            role="link"
                            tabIndex={0}
                          >
                            <VendorCard
                              vendor={vendor}
                              matchedCategory={category}
                              requestId={urlRequestId}
                              selectionMode={isSelectionMode}
                              selectionStatus={selectionStatus}
                              isSelecting={isSelecting}
                              isDisabled={isDisabled}
                              onSelect={handleSelectVendor}
                            />
                          </div>
                        );
                      })}
                    </div>

                    {/* Selection mode: skip link at bottom */}
                    {isSelectionMode && (
                      <div className={css.selectionBottomActions}>
                        <button
                          className={css.skipLink}
                          onClick={() => history.push('/dashboard')}
                          type="button"
                        >
                          Skip for now — I'll choose later
                        </button>
                      </div>
                    )}

                    {!isSelectionMode && (
                      <div className={css.bottomCta}>
                        <p className={css.ctaText}>
                          Don't see what you're looking for?
                        </p>
                        <NamedLink
                          name="RequestIntakeBasePage"
                          className={css.ctaButton}
                        >
                          Submit a Request
                        </NamedLink>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={css.emptyState}>
                    <div className={css.emptyIcon}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                    </div>
                    <h2 className={css.emptyTitle}>
                      {isSelectionMode ? "No Contractors in Your Area Yet" : "We're building our network in your area."}
                    </h2>
                    <p className={css.emptyText}>
                      {isSelectionMode
                        ? "We don't have contractors matching your request yet, but we're growing fast! We'll notify you when one joins. You can also check back from your dashboard."
                        : "We don't have contractors offering this service near you yet. Submit a request and we'll notify you as soon as a matching professional joins — and we'll keep working to find you help in the meantime."
                      }
                    </p>
                    <div className={css.emptyActions}>
                      {isSelectionMode ? (
                        <NamedLink name="DashboardPage" className={css.primaryButton}>
                          Go to Dashboard
                        </NamedLink>
                      ) : (
                        <>
                          <NamedLink
                            name="RequestIntakeBasePage"
                            className={css.primaryButton}
                          >
                            Submit a Request
                          </NamedLink>
                          <button
                            className={css.secondaryButton}
                            onClick={() => handleCategoryClick(null)}
                            type="button"
                          >
                            Browse All Contractors
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel: Map (desktop only) */}
            <div className={css.mapPanel}>
              <VendorMap
                vendors={visibleVendors}
                activeVendorId={activeVendorId}
                onVendorClick={handleMapVendorClick}
              />
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = (state) => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
    vendors: selectVendors(state),
    fetchInProgress: selectFetchInProgress(state),
    fetchError: selectFetchError(state),
    category: selectCategory(state),
    county: selectCounty(state),
    services: selectServices(state),
    address: selectAddress(state),
    lastFetchKey: selectLastFetchKey(state),
    vendorSelections: selectVendorSelections(state),
    selectInProgress: selectSelectInProgress(state),
    selectError: selectSelectError(state),
    releaseInProgress: selectReleaseInProgress(state),
    releaseError: selectReleaseError(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  onFetchVendors: (params) => dispatch(fetchVendorsThunk(params)),
  onSelectVendor: (params) => dispatch(selectVendorThunk(params)),
  onSetVendorSelections: (selections) => dispatch(setVendorSelections(selections)),
  onReleaseVendor: (params) => dispatch(releaseVendorThunk(params)),
});

const MatchedVendorsPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(MatchedVendorsPageComponent);

export default MatchedVendorsPage;
